import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { LogoMark } from '../components/LogoMark';
import { SectionHeading } from '../components/SectionHeading';
interface AboutProps {
  onDownload: () => void;
}
const PRINCIPLES = [{
  title: 'Clarity beats complexity',
  body: 'If the team cannot explain the system simply, the system is not working yet.'
}, {
  title: 'Inputs shape decisions',
  body: 'Every decision inherits the quality of the data, mapping and observations behind it.'
}, {
  title: 'Interpretation needs structure',
  body: 'Good geological thinking deserves a process, not just instinct under pressure.'
}, {
  title: 'Feedback should change future work',
  body: 'Reconciliation that never changes behaviour is just reporting.'
}, {
  title: 'Improvement must be deliberate',
  body: 'Better systems are built on purpose, with a rhythm — not by accident.'
}, {
  title: 'Systems are built by people, not documents alone',
  body: 'Standards live in behaviour. People make systems real.'
}];
export function About({
  onDownload
}: AboutProps) {
  return <>
      <section className="relative w-full overflow-hidden bg-cream py-16 lg:py-24" data-id="element-146">
        <div className="mgs-grid absolute inset-0" aria-hidden data-id="element-147" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8" data-id="element-148">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:gap-12" data-id="element-149">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-charcoal text-3xl font-black text-cream lg:h-32 lg:w-32 lg:text-4xl" aria-hidden data-id="element-150">
              JB
            </div>
            <div className="max-w-2xl" data-id="element-151">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-152">
                <LogoMark className="h-3.5 w-3.5" data-id="element-153" /> Better Systems – Better
                Decisions – Better Outcomes
              </p>
              <h1 className="mt-3 text-4xl font-black leading-[1.05] tracking-tight text-charcoal sm:text-5xl" data-id="element-154">
                About Jordan Biglin and Mine Geology Systems
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-warmgray" data-id="element-155">
                Mine Geology Systems (MGS) is an evolving operational framework
                focused on improving structured thinking, feedback and
                continuous learning within mine geology systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-cream pb-20 pt-4 lg:pb-28" data-id="element-156">
        <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-157">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-158">
            <div className="lg:col-span-7" data-id="element-159">
              <SectionHeading eyebrow="The geologist behind MGS" title="14 years underground, in the model, and at the face." data-id="element-160" />
              <div className="mt-6 space-y-4 text-base leading-relaxed text-warmgray" data-id="element-161">
                <p data-id="element-162">
                  Jordan Biglin is a geologist with approximately 14 years of
                  experience across underground mining, resource development and
                  mineral exploration, primarily within gold and copper
                  operations.
                </p>
                <p data-id="element-163">
                  Throughout his career, he has held technical and leadership
                  roles with companies including{' '}
                  <span className="font-semibold text-charcoal" data-id="element-164">
                    Northern Star Resources
                  </span>
                  ,{' '}
                  <span className="font-semibold text-charcoal" data-id="element-165">
                    Spartan Resources
                  </span>{' '}
                  and{' '}
                  <span className="font-semibold text-charcoal" data-id="element-166">
                    Greatland Resources
                  </span>
                  .
                </p>
                <p data-id="element-167">
                  Jordan has also in the past operated his own geological
                  consulting business,{' '}
                  <span className="font-semibold text-charcoal" data-id="element-168">Geolance</span>,
                  and founded{' '}
                  <span className="font-semibold text-charcoal" data-id="element-169">
                    Mining Pages
                  </span>
                  , an online directory connecting mining consultants,
                  contractors and service providers with the mining industry.
                </p>
                <p data-id="element-170">
                  Mine Geology Systems is the distillation of that experience —
                  built from the practical reality of doing the work across
                  underground and open pit operations, not theory.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5" data-id="element-171">
              <div className="rounded-2xl border border-charcoal/10 bg-sand p-7 shadow-card" data-id="element-172">
                <p className="text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-173">
                  Background at a glance
                </p>
                <dl className="mt-5 space-y-5" data-id="element-174">
                  {[{
                  k: '~14 years',
                  v: 'Across underground mining, resource development and mineral exploration.'
                }, {
                  k: 'Gold & copper',
                  v: 'Primary commodity focus across multiple operations.'
                }, {
                  k: 'Technical & leadership roles',
                  v: 'Northern Star Resources, Spartan Resources and Greatland Resources.'
                }, {
                  k: 'Founder',
                  v: 'Geolance (geological consulting) and Mining Pages (industry directory).'
                }].map(row => <div key={row.k} className="flex items-start gap-3" data-id="element-175">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow" data-id="element-176" />
                      <div data-id="element-177">
                        <dt className="text-sm font-bold text-charcoal" data-id="element-178">
                          {row.k}
                        </dt>
                        <dd className="mt-0.5 text-sm leading-relaxed text-warmgray" data-id="element-179">
                          {row.v}
                        </dd>
                      </div>
                    </div>)}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-sand py-20 lg:py-28" data-id="element-180">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8" data-id="element-181">
          <div data-id="element-182">
            <SectionHeading eyebrow="The idea" title="A connected operational system, not a list of tasks." data-id="element-183" />
            <div className="mt-6 space-y-4 text-base leading-relaxed text-warmgray" data-id="element-184">
              <p data-id="element-185">
                The idea behind MGS is simple: mine geology is not just a
                collection of individual technical tasks, it is a connected
                operational system involving inputs, interpretation, decisions,
                feedback and improvement — with an operating layer bringing it
                all together.
              </p>
              <p data-id="element-186">
                When one part of the system weakens, the impacts often appear
                elsewhere. Reconciliation issues, inconsistent interpretation,
                QAQC issues and operational challenges are frequently symptoms
                of broader system interactions rather than isolated technical
                problems.
              </p>
              <p data-id="element-187">
                MGS was developed from a practical operational perspective
                across underground and open pit geology, with a focus on:
              </p>
              <ul className="space-y-2" data-id="element-188">
                {['Systems thinking', 'Operational awareness', 'Continuous improvement', 'Structured feedback loops', 'Practical geological decision-making'].map(b => <li key={b} className="flex items-start gap-3 text-sm font-medium text-charcoal" data-id="element-189">
                    <LogoMark className="mt-0.5 h-4 w-4 shrink-0" data-id="element-190" />
                    {b}
                  </li>)}
              </ul>
              <p data-id="element-191">
                MGS is intentionally practical in its approach. The focus is not
                on creating complexity, but on improving visibility, consistency
                and learning within operational geology systems. The framework
                continues to evolve through operational observations, practical
                application, shared learning and ongoing refinement over time.
              </p>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-6" data-id="element-192">
            <div className="rounded-2xl border border-charcoal/10 bg-cream p-7 shadow-card lg:p-9" data-id="element-193">
              <p className="text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-194">
                Purpose
              </p>
              <p className="mt-4 text-2xl font-black leading-snug text-charcoal" data-id="element-195">
                The purpose of MGS is to:
              </p>
              <ul className="mt-4 space-y-2.5" data-id="element-196">
                {['Encourage structured operational thinking', 'Improve system awareness', 'Support continuous improvement', 'Promote operational learning', 'Help identify weaknesses before they become major issues'].map(b => <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-warmgray" data-id="element-197">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow" data-id="element-198" />
                    {b}
                  </li>)}
              </ul>
              <Button to="/framework" variant="secondary" className="mt-6" data-id="element-199">
                Explore the Framework
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-cream py-20 lg:py-28" data-id="element-200">
        <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-201">
          <SectionHeading eyebrow="Beliefs" title="The principles behind the framework." body="Six beliefs that shape every resource, case study and field note MGS publishes." data-id="element-202" />
          <div className="mt-12 grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-203">
            {PRINCIPLES.map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="flex h-full flex-col rounded-xl border border-charcoal/10 bg-sand p-6 shadow-card" data-id="element-204">
                <LogoMark className="h-5 w-5" data-id="element-205" />
                <h3 className="mt-4 text-lg font-bold leading-snug text-charcoal" data-id="element-206">
                  {p.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-warmgray" data-id="element-207">
                  {p.body}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="w-full bg-charcoal py-20 lg:py-24" data-id="element-208">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8" data-id="element-209">
          <h2 className="text-3xl font-black leading-tight text-cream sm:text-4xl" data-id="element-210">
            Start with the guide, or just say hello.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-cream/80" data-id="element-211">
            Download the free framework guide, or send Jordan a question — every
            message gets read.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-212">
            <Button variant="primary" onClick={onDownload} data-id="element-213">
              Download the Free MGS Toolkit
            </Button>
            <Button to="/contact" variant="ghost" className="text-cream hover:bg-cream/10" data-id="element-214">
              Start a conversation
            </Button>
          </div>
          <p className="mx-auto mt-10 max-w-2xl text-xs leading-relaxed text-cream/50" data-id="element-215">
            MGS provides general operational frameworks, tools and educational
            content intended to support structured thinking and continuous
            improvement in mine geology systems. MGS content is not
            site-specific technical advice and should not replace professional
            judgement, operational procedures, or company standards.
          </p>
        </div>
      </section>
    </>;
}